import { get } from 'lodash'
import { IOpportunityData } from 'src/components/pages/drivingopportunities/types'
import { IDENTIFY_TIMEOUT } from './constants'
import { DeliveryIssueListItem } from './models/types'
import * as DA from './typeModules/events'
import { formatToE164 } from './utilities/phoneNumber'
import { segmentIdentify, trackWithInfo as trackEvent } from './instrumentation'
/* TODO: add these back when we upgrade Typescript to support unknown */
// import { ThunkAction } from 'redux-thunk'
// import { AnyAction } from 'redux'
// import { RootState } from './reducers'
import { WebviewPages } from './components/pages/webview/constants'

interface IWindowWithView extends Window {
  view: any
}

declare let window: IWindowWithView

/* https://redux.js.org/usage/usage-with-typescript#type-checking-redux-thunks -- unknown/Any expected here  */
// export type Event = (data?: Record<string, string | Error>) => ThunkAction<void, RootState, unknown, AnyAction>

export const trackDasherApplyPageView = () => {
  // Legacy naming scheme kept around for data-consistency purposes.
  return trackEvent(
    'dasher_application_page',
    ({ referrer, ip, guaranteedEarningsCampaign }) => {
      return {
        ip,
        referrerId: referrer.id,
        referrerName: referrer.name,
        ...guaranteedEarningsCampaign,
      }
    }
  )
}

export const identifyDasher = (data: any = {}) => {
  return new Promise<void>(resolve => {
    const timeout = setTimeout(() => {
      resolve()
    }, IDENTIFY_TIMEOUT)

    segmentIdentify(data.uniqueLink, {}, {}, () => {
      clearTimeout(timeout)
      resolve()
    })
  })
}

export const trackCreateConsumerAccountFailure = () => {
  return trackEvent('DA_create_consumer_account_error')
}

export const trackUpdateDasherApplicantFailure = error => {
  return trackEvent('DA_update_dasher_applicant_error', () => ({
    ...error,
  }))
}

export const trackSubmitProfile = (application, args) => {
  if (dataIsValid(application)) {
    return trackEvent('DA_submit_profile', state => ({
      email: application.email,
      submarket: application.submarket,
      market: application.market,
      vehicleType: args.vehicleType,
      firstName: args.firstName,
      lastName: args.lastName,
      middleName: args.middleName,
      referredBy: args.referredBy,
      market_type: typeof application.market, // Added to debug issues with iterable can be removed once iterable data is clean
      submarket_type: typeof application.submarket, // Added to debug issues with iterable can be removed once iterable data is clean
      experiments: {
        address_collection_after_dash: get(
          state,
          'experiments.address_collection_after_dash'
        ),
      },
    }))
  }
  return null
}

export const trackSubmitProfileFailure = data => {
  return trackEvent('DA_submit_profile_error', () => data)
}

export const trackSignupAndSubmitProfileFailure = error => {
  return trackEvent('DA_signup_and_submit_profile_error', () => ({
    fieldErrors: error.fieldErrors,
  }))
}

export const trackLoadDasherApplicationFailure = error => {
  return trackEvent('DA_load_dasher_application_error', () => ({
    ...error,
  }))
}

export const trackReferrerButtonClicked = showField => {
  return trackEvent('DA_referrer_button_clicked', () => ({
    showField,
  }))
}

const dataIsValid = data => {
  if (
    data &&
    (typeof data.market === 'string' || typeof data.submarket === 'string')
  ) {
    trackEvent('DA_invalid_tracking_data', () => ({
      market: data.market,
      submarket: data.submarket,
      // Added to debug issues with iterable
      // can be removed once iterable data is clean
      market_type: typeof data.market,
      submarket_type: typeof data.submarket,
      // End of debug of issue with iterable
    }))
    return false
  }
  return true
}

export const trackProfileFormRendered = data => {
  if (dataIsValid(data)) {
    return trackEvent('DA_profile_form_rendered', () => ({
      hasConsumerAccount: data.hasConsumerAccount,
      email: data.email,
      phone: data.phoneNumber,
      market: data.market,
      submarket: data.submarket,
      // Added to debug issues with iterable
      // can be removed once iterable data is clean
      market_type: typeof data.market,
      submarket_type: typeof data.submarket,
      // End of debug of issue with iterable
    }))
  }
  return null
}

export const trackDasherContinueApplication = (
  data: DA.DasherContinueApplication
) => {
  return trackEvent('DA_return_to_application_modal', () => ({
    ...data,
  }))
}

// todo -- examine why this needed to return a promise
export const trackDasherReturnToApplicationSuccess = () => {
  return trackEvent('DA_return_to_application_success')
}

export const trackDasherReturnToApplicationFailure = (
  error: { fieldErrors?: string },
  applicationCredentials: {
    phoneNumber: string
    email: string
  }
) => {
  const { phoneNumber, email } = applicationCredentials
  return trackEvent('DA_return_to_application_error', () => ({
    phoneNumber,
    email,
    fieldErrors:
      typeof error.fieldErrors !== 'undefined' ? error.fieldErrors : error,
  }))
}

export const trackDasherLogin = (data: DA.DasherLogin) => {
  return trackEvent('DA_login', () => ({
    ...data,
  }))
}

export const trackDasherApplyNonMarket = data => {
  return trackEvent('DA_not_in_market_error', () => ({
    submarket: data.submarket,
    zip_code: data.zipCode,
  }))
}

export const trackDasherApplySubmissionSuccess = data => {
  return trackEvent('submit_dasher_app_event', () => ({
    phoneNumber: formatToE164(data.phoneNumber),
    phone: data.phoneNumber,
    email: data.email,
    market: data.market,
    submarket: data.submarket,
  }))
}

// this gets called by dispatch, therefor it doesn't need to be returned
export const trackDasherApplySubmissionFailure = error => {
  trackEvent('DA_submit_dasher_app_error', () => ({
    fieldErrors: error.fieldErrors,
  }))
}

export const trackDasherApply = (data: DA.DasherApply) => {
  return trackEvent('DA_submit_dasher_apply', () => ({
    ...data,
  }))
}

// Has to read campaign data from the window becuase it is
// intentionally not read into state for expired campaigns
export const trackGEExpiredModalRender = () =>
  trackEvent(
    'dasher_application_page_expired_campaign_modal_render',
    () => window.view.guaranteed_earnings_campaign
  )

// Has to read campaign data from the window becuase it is
// intentionally not read into state for expired campaigns
export const trackGEExpiredModalDismiss = () =>
  trackEvent(
    'dasher_application_page_expired_campaign_modal_click_continue',
    () => window.view.guaranteed_earnings_campaign
  )

type GEModalData = {
  campaign: any
  applicationData: any
}

export const trackGEInvalidZipContinue = (modalData: GEModalData) => {
  return trackEvent(
    'submit_dasher_app_event_invalid_zip_modal_click_continue',
    () => ({
      ...modalData.campaign,
      zip_code: modalData.applicationData.zipCode,
    })
  )
}

export const trackGEInvalidZipDismiss = (modalData: GEModalData) => {
  return trackEvent(
    'submit_dasher_app_event_invalid_zip_modal_click_cancel',
    () => ({
      ...modalData.campaign,
      zip_code: modalData.applicationData.zipCode,
    })
  )
}

export const trackGEInvalidZipModalRender = (modalData: GEModalData) => {
  return trackEvent(
    'submit_dasher_app_event_invalid_zip_ge_modal_render',
    () => ({
      ...modalData.campaign,
      zip_code: modalData.applicationData.zipCode,
    })
  )
}

export const trackGEInvalidZipTermsClick = (modalData: GEModalData) => {
  return trackEvent(
    'submit_dasher_app_event_invalid_zip_modal_click_terms',
    () => ({
      ...modalData.campaign,
      zip_code: modalData.applicationData.zipCode,
    })
  )
}

export const trackGESubmissionFailureInvalidZip = zip => {
  return trackEvent('submit_dasher_app_event_invalid_zip', state => ({
    ...state.guaranteedEarningsCampaign,
    zip_code: zip,
  }))
}

export const trackConfirmationPageTapDownloadApp = () => {
  return trackEvent('DA_confirmation_page_tap_download_app', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
    country: state.country,
  }))
}

export const trackOrientationDetailsRendered = () => {
  return trackEvent('DA_orientation_details_page_rendered', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
  }))
}

export const trackInstantDashBanner = () => {
  return trackEvent('da_instant_dash_confirmation_page_render', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
    background_check_status: state.application.background_check_results,
  }))
}

export const trackInstantDashBannerDropshippingEvent = () => {
  return trackEvent('DA_dxds_progress.da_dxds_confirmation_page', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
    background_check_status: state.application.background_check_results,
  }))
}
export const trackDropshippingEvent = (step: string, data?: object) => {
  return trackEvent('DA_dxds_progress', state => ({
    ...data,
    step,
    email: get(state, 'application.email'),
    submarket_id: get(state, 'application.submarket'),
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
    country: state.country,
  }))
}

export const trackGoBack = (currentStep: string) => {
  return trackEvent('DA_go_back', () => ({
    previousStep: currentStep,
  }))
}

export const trackConfirmationPageRendered = data => {
  return trackEvent('DA_confirmation_page_rendered', state => {
    let version = 1
    if (state.experiments.dropshipping_status) {
      version = 2
    }
    if (state.experiments.bank_onboarding) {
      version = 3
    }
    return {
      ...data,
      version,
      submarket_id: state.application.submarket,
      unique_link: state.uniqueLink,
      dasher_id: state.dasherId,
      country: state.country,
      applicationStatus: state.applicationStatus.data.onboardingProgress,
      thinFileStatus: state.applicationStatus.data.thinFileStatus,
    }
  })
}

export const trackSpaceEntry = () => {
  return trackEvent('DA_zipcode_space')
}

export const trackConfirmationPageSectionClicked = (section, data) => {
  return trackEvent(`DA_confirmation_page_${section}_clicked`, state => ({
    data: { ...data },
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
    country: state.country,
  }))
}

export const trackConfirmationPageClick = (name, data) => {
  return trackEvent(`DA_confirmation_page_${name}_clicked`, state => ({
    data,
    country: state.country,
  }))
}

export const trackForgotPassword = () => {
  return trackEvent('DA_forgot_password')
}

export const trackSubmitBackgroundCheckError = error => {
  return trackEvent('DA_submit_background_check_error', () => ({
    ...error.response.data,
  }))
}

export const trackCreateDasherAccountError = error => {
  const response = error.response
  // For handling GRPC errors on local dev that send too
  // much data into Amplitude
  // Should work fine on prod
  if (response && response.data && response.data.length < 1000) {
    return trackEvent('DA_create_dasher_account_error', () => ({
      ...error.response.data,
    }))
  }
  return trackEvent('DA_create_dasher_account_error')
}

export const trackVehicleTypeSelected = vehicleType => {
  return trackEvent('DA_vehicle_type_selected', state => ({
    vehicleType,
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
  }))
}

export const trackInstantDashAppDownload = os => {
  return trackEvent('da_instant_dash_app_download', state => ({
    os,
    submarket_id: state.application.submarket,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
  }))
}

export const trackTappedHelpArticle = () => {
  return trackEvent('DA_confirmation_page_tap_help_article', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
  }))
}

export const trackTappedHowToDashVideo = () => {
  return trackEvent('DA_confirmation_page_tap_video', state => ({
    submarket_id: state.application.submarket,
    market_id: state.application.market,
    unique_link: state.uniqueLink,
    dasher_id: state.dasherId,
  }))
}

export const trackSubmitBackgroundCheck = application => {
  return trackEvent('DA_submit_background_check', () => ({
    phone: application.phoneNumber,
    email: application.email,
    submarket: application.submarket,
    market: application.market,
  }))
}

export const trackBackgroundCheckFormRendered = country => {
  return trackEvent('DA_background_check_form_rendered', state => ({
    country,
    email: state.application.email,
  }))
}

export const trackCanadaCheckrRedirectSuccess = (callback?) => {
  if (callback) {
    return trackEvent('da_ca_checkr_redirect_success', () => ({}), callback)
  }

  return trackEvent('da_ca_checkr_redirect_success', () => ({}))
}

export const trackPendingBackgroundCheckFormRendered = () => {
  return trackEvent('DA_pending_background_check_page_rendered')
}

export const trackScheduleOrientationFormRendered = () => {
  return trackEvent('DA_schedule_orientation_form_rendered')
}

export const trackOrientationBooking = orientation => {
  const data = {
    address: orientation.address,
    endTime: orientation.endTime.toISOString(),
    startTime: orientation.startTime.toISOString(),
    startingPoint: orientation.startingPoint,
    timezone: orientation.timezone,
  }
  return trackEvent('DA_book_orientation', state => ({
    ...data,
    email: get(state, 'application.email'),
  }))
}

export const trackOrientationBookingError = error => {
  return trackEvent('DA_book_orientation_error', () => error.data)
}

export const trackApplicationClosed = submarket => {
  return trackEvent('DA_application_closed_rendered', () => ({
    submarket,
  }))
}

export type PageTrackingProps = {
  pageType: string
  data: IOpportunityData
}

export const trackDrivingOpportunitiesRendered = (page: PageTrackingProps) => {
  const data = {
    pageType: page.pageType,
    ...page.data,
  }

  return trackEvent('DA_driving_opportunities_rendered', () => data)
}

export const trackRenderRedirectToCheckr = () => {
  return trackEvent('DA_render_redirect_to_checkr')
}

export const trackRedirectToCheckr = () => {
  return trackEvent('DA_redirect_to_checkr')
}

// This event is deprecated
export const trackRedCardBeginRendered = () => {
  return trackEvent('DA_redcard_begin_rendered')
}

// This event is deprecated
export const trackRedCardBeginSubmit = () => {
  return trackEvent('DA_redcard_begin_submit')
}

export const trackRedCardActivateRendered = () => {
  return trackEvent('DA_redcard_activate_rendered')
}

export const trackRedCardActivateSubmit = () => {
  return trackEvent('DA_redcard_activate_submit')
}

export const trackRedCardActivateError = error => {
  const e = {
    message: error.message,
    data: {
      ...error.response.data,
    },
  }

  return trackEvent('DA_redcard_activate_error', () => e)
}

export const trackRedCardActivateLearnMore = state => {
  // state is 'opened' or 'closed'
  return trackEvent(`DA_redcard_activate_learnmore_modal_${state}`)
}

export const trackRedCardActivateClosed = () => {
  return trackEvent('DA_redcard_activate_closed')
}

export const trackLanguageChanged = language => {
  return trackEvent('DA_language_changed', () => ({
    language,
  }))
}

export const trackRedCardActivateGoBack = () => {
  return trackEvent('DA_redcard_activate_go_back')
}

export const trackBankAccountFormRendered = (
  data: DA.BankAccountFormPropsRendered
) => {
  return trackEvent('DA_add_bank_account_form_rendered', () => data)
}

export const trackBankAccountFormClicked = (
  data: DA.BankAccountFormPropsClicked
) => {
  return trackEvent('DA_add_bank_account_form_clicked', () => data)
}

export const trackBankAccountFormSubmit = (
  data: DA.BankAccountFormPropsSubmit
) => {
  return trackEvent('DA_add_bank_account_form_submit', () => data)
}

export const trackBankAccountFormError = (
  data: DA.BankAccountFormPropsError
) => {
  return trackEvent('DA_add_bank_account_form_error', () => data)
}

export const trackBankAdditionalVerification = (
  data: DA.BankAdditionalVerification
) => {
  return trackEvent('DA_bank_additional_verification', () => data)
}

export const trackPaymentAccount = (data: DA.PaymentAccount) => {
  return trackEvent('DA_bank_payment_accounts', () => data)
}

export const trackInAppEducationNavigated = (data: DA.InAppEducation) => {
  return trackEvent('in_app_education_navigated', () => data)
}

export const trackWebviewClosed = () => {
  return trackEvent('webview_closed')
}

export const trackLandingPageFAQ = (data: DA.LandingPageFAQ) => {
  return trackEvent('DA_landing_page_faq', () => ({
    ...data,
  }))
}

export const trackInstantDashSplashViewed = () => {
  return trackEvent('instant_dash_splash_viewed')
}

export const trackActivationSplashViewed = () => {
  return trackEvent('activation_splash_viewed')
}

export const trackInsuranceTermsModal = (data: DA.InsuranceTermsModal) => {
  return trackEvent('insurance_modal_action', () => data)
}

export const trackHowToDashNavigated = (data: DA.SlideEvents) => {
  return trackEvent('DA_how_to_dash_navigated', () => data)
}

export const trackBackgroundCheckSteps = (data: DA.TrackBackgroundCheck) => {
  return trackEvent('background_check_split_action', () => data)
}

export const trackIdentityVerificationSteps = (
  data: DA.IdentityVerificationEvents
) => {
  return trackEvent('DA_track_idv_steps', () => data)
}

export const trackLandingPageHeader = (data: DA.LandingPageHeader) => {
  return trackEvent('DA_landing_page_header', () => data)
}

export const trackLandingPageSignUpDetails = (
  data: DA.LandingPageSignUpDetails
) => {
  return trackEvent('DA_landing_page_signup_details', () => data)
}

export const trackFastPaySetup = (data: DA.FastPaySetup) => {
  return trackEvent('DA_fast_pay_setup', () => data)
}

export const trackNeedsVehicleInfo = (data: DA.NeedsVehiclInfo) => {
  return trackEvent('DA_needs_vehicle_info', () => data)
}

export const trackSkipVehicleInfo = () => {
  return trackEvent('DA_needs_vehicle_info_skipped')
}

export const trackGetSalesContactForm = (data: DA.GetSales) => {
  return trackEvent('DA_get_sales_contact', () => data)
}

export const trackBottomOfFunnelWaitlist = (data: DA.BoFWaitlist) => {
  return trackEvent('DA_bof_waitlist', () => data)
}

export const trackIaeFAQClick = (data: { link: string }) => {
  return trackEvent('DA_iae_faq_clicked', () => data)
}

export const trackPolicyViolations = (data: DA.PolicyViolation) => {
  return trackEvent('dx_policy_violation', state => ({
    dasher_id: state.dasherId,
    ...data,
  }))
}

export const trackWebviewMultiFactorAuth = (
  data: DA.WebviewMultiFactorAuth
) => {
  return trackEvent('DA_mfa_challenge', () => data)
}

export const trackSignupMultiFactorAuth = (data: DA.SignupMultiFactorAuth) => {
  return trackEvent('DA_mfa_challenge', () => data)
}

export const trackGetDeliveryIssuesListError = error => {
  return trackEvent('get_delivery_issues_list_error', state => ({
    error,
    dasher_id: state.dasherId,
  }))
}

export const trackDeliveryIssuesWithoutId = (
  issues: DeliveryIssueListItem[]
) => {
  return trackEvent('delivery_issues_without_id', state => ({
    dasher_id: state.dasherId,
    issues_without_id: issues,
  }))
}

export const trackSafetyCourse = (
  action: DA.EventActions,
  name: DA.SafetyCourseSections,
  courseId: number,
  data?: object
) => {
  return trackEvent('dx_safety_course', state => ({
    action,
    name,
    courseId,
    dasher_id: state.dasherId,
    email: state.application.email,
    ...data,
  }))
}

export const trackAddressCollection = (
  action: DA.EventActions,
  name: DA.AddressCollectionScreens,
  data?: object
) => {
  return trackEvent('dx_activation_address', state => ({
    action,
    name,
    dasher_id: state.dasher_id,
    ...data,
  }))
}

export const trackProgressionCheckRendered = (
  action: DA.EventActions,
  step: string,
  applicantId: string,
  clientDriven: boolean
) => {
  return trackEvent('DA_progression_check_rendered', () => ({
    action,
    active_step: step,
    applicant_id: applicantId,
    client_driven: clientDriven,
  }))
}

export const trackActivatedNoFirstDash = (
  action: DA.EventActions,
  page: DA.ActivatedNoFirstDashScreens,
  data?: object
) => {
  return trackEvent('dx_activated_no_first_dash', state => ({
    action,
    page,
    dasher_id: state.dasher_id,
    ...data,
  }))
}

export const trackEditBGCInfo = (data: DA.EditBGCInfo) =>
  trackEvent('DA_edit_bgc_info', () => data)

// GRPD: General Data Protection Regulation
export const trackGDPR = (data: DA.GDPR) =>
  trackEvent('dx_dasher_gdpr', () => data)

// RTI: Right To Information
export const trackRTI = (data: DA.RTI) =>
  trackEvent('dx_dasher_ccpa_rti', () => data)

// RTF: Right To Forget
export const trackRTF = (data: DA.RTI) =>
  trackEvent('dx_dasher_ccpa_rtf', () => data)

/*
/**
  * Webview lifecycle events
  */
/**
 * Invoke when front end first encounters the webview render path, before trying to make any api calls or resolve which page we're on
 */
export const trackWebviewBootstrapStart = () =>
  trackEvent('dx_webview_bootstrap_start')

/**
 * Invoke when initial api calls to mount page fails
 */
export const trackWebviewBootstrapFail = ({ error }: { error: Error }) =>
  trackEvent('dx_webview_bootstrap_fail', () => ({ error }))

/**
 * Invoke when the our front end router decides which view is to be shown inside the webview
 */
export const trackWebviewRouterResolve = ({
  pageName,
}: {
  pageName: WebviewPages
}) => trackEvent('dx_webview_router_resolved', () => ({ page: pageName }))

/**
 *
 * Workflow tracking events
 *
 */

export type WorkflowTrackingProps = {
  stepName: string
  workflowId: string
  uniqueLink: string
  errorMessage?: string
  errorCode?: string
}

/**
 *
 * fired when a new step in workflow is rendered
 *
 */
export const trackWorkflowStepRendered = (props: WorkflowTrackingProps) => {
  return trackEvent('workflow_step_rendered', () => ({
    step_name: props.stepName,
    workflow_id: props.workflowId,
    unique_link: props.uniqueLink,
  }))
}

/**
 *
 * fired when a step is submitted successfully
 *
 */
export const trackWorkflowStepSubmitSuccess = (props: WorkflowTrackingProps) => {
  return trackEvent('workflow_step_submit_success', () => ({
    step_name: props.stepName,
    workflow_id: props.workflowId,
    unique_link: props.uniqueLink,
  }))
}

/**
 *
 * fired when a step is submitted and there is an error
 *
 */
export const trackWorkflowStepSubmitFailure = (
  props: WorkflowTrackingProps
) => {
  return trackEvent('workflow_step_submit_failure', () => ({
    step_name: props.stepName,
    workflow_id: props.workflowId,
    unique_link: props.uniqueLink,
    error_message: props.errorMessage,
    error_code: props.errorCode
  }))
}
